// Selector de rango de fecha con presets (Hoy, Ayer, Últimos 7/15/30 días,
// Rango personalizado). Reutilizable por la Lista y el Kanban de validación.
//
// Controlado: recibe { range, date_from, date_to } y notifica cambios vía
// onChange({ range, date_from, date_to }). Para "custom" muestra dos date
// pickers (react-flatpickr). El rango es requerido: siempre hay un valor.
import React, { useMemo, useRef, useState, useEffect } from "react"
import PropTypes from "prop-types"
import {
  Dropdown, DropdownToggle, DropdownMenu, Button, Row, Col, Label,
} from "reactstrap"
import { DATE_RANGE_PRESETS, dateRangeValue } from "./filterDefaults"

const DateRangeFilter = ({ t, value, onChange }) => {
  const { range, date_from, date_to } = value
  const [open, setOpen] = useState(false)
  // Estado local del rango personalizado mientras el dropdown está abierto.
  const [customFrom, setCustomFrom] = useState(date_from || "")
  const [customTo, setCustomTo] = useState(date_to || "")
  const rootRef = useRef(null)

  useEffect(() => {
    setCustomFrom(date_from || "")
    setCustomTo(date_to || "")
  }, [date_from, date_to])

  const presetLabel = useMemo(() => {
    const p = DATE_RANGE_PRESETS.find((x) => x.id === range)
    return p ? (t(p.labelKey) || p.labelFallback) : (t("Today") || "Hoy")
  }, [range, t])

  // Texto del botón: etiqueta del preset + rango efectivo.
  const summary = useMemo(() => {
    if (date_from && date_to) {
      return date_from === date_to ? date_from : `${date_from} → ${date_to}`
    }
    return ""
  }, [date_from, date_to])

  const selectPreset = (id) => {
    if (id === "custom") {
      // Cambia a custom pero mantiene el dropdown abierto para elegir fechas.
      onChange({ range: "custom", date_from: customFrom || date_from, date_to: customTo || date_to })
      return
    }
    const r = dateRangeValue(id)
    onChange({ range: id, date_from: r.date_from, date_to: r.date_to })
    setOpen(false)
  }

  // Inputs de fecha NATIVOS (<input type="date">): su valor ya es YYYY-MM-DD,
  // igual que ``iso()``. Se usan en lugar de Flatpickr porque este renderiza su
  // calendario en un portal fuera del DropdownMenu; al hacer click en un día,
  // reactstrap lo interpretaba como "click afuera" y cerraba el dropdown antes
  // de registrar la selección (el día elegido no se reflejaba).
  const onPickFrom = (e) => setCustomFrom(e.target.value || "")
  const onPickTo = (e) => setCustomTo(e.target.value || "")

  const applyCustom = () => {
    if (!customFrom || !customTo) return
    // Normaliza el orden (from <= to).
    const from = customFrom <= customTo ? customFrom : customTo
    const to = customFrom <= customTo ? customTo : customFrom
    onChange({ range: "custom", date_from: from, date_to: to })
    setOpen(false)
  }

  const clearCustom = () => {
    setCustomFrom("")
    setCustomTo("")
  }

  return (
    <Dropdown isOpen={open} toggle={() => setOpen((v) => !v)} innerRef={rootRef}>
      <DropdownToggle caret color="light" className="sv-date-toggle w-100 text-start d-flex align-items-center justify-content-between">
        <span className="text-truncate">
          <i className="bx bx-calendar me-1" />
          {presetLabel}{summary ? ` · ${summary}` : ""}
        </span>
      </DropdownToggle>
      <DropdownMenu className="p-0" style={{ minWidth: 320 }}>
        <div className="d-flex">
          {/* Lista de presets */}
          <div className="border-end" style={{ minWidth: 150 }}>
            {DATE_RANGE_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`dropdown-item ${range === p.id ? "active" : ""}`}
                onClick={() => selectPreset(p.id)}
              >
                {t(p.labelKey) || p.labelFallback}
              </button>
            ))}
          </div>

          {/* Panel del rango personalizado. stopPropagation en mousedown/click
              evita que reactstrap trate la interacción con los inputs/calendario
              como "click afuera" y cierre el dropdown antes de registrar la fecha. */}
          {range === "custom" && (
            <div
              className="p-2"
              style={{ minWidth: 170 }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <Row className="g-2">
                <Col xs={12}>
                  <Label className="form-label mb-1 small">{t("StartDate") || "Fecha de inicio"}</Label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={customFrom}
                    max={customTo || undefined}
                    onChange={onPickFrom}
                  />
                </Col>
                <Col xs={12}>
                  <Label className="form-label mb-1 small">{t("EndDate") || "Fecha de fin"}</Label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={customTo}
                    min={customFrom || undefined}
                    onChange={onPickTo}
                  />
                </Col>
              </Row>
              <div className="d-flex justify-content-between mt-2">
                <Button size="sm" color="light" onClick={clearCustom}>{t("Clear") || "Limpiar"}</Button>
                <Button size="sm" color="primary" onClick={applyCustom} disabled={!customFrom || !customTo}>
                  {t("Apply") || "Aplicar"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DropdownMenu>
    </Dropdown>
  )
}

DateRangeFilter.propTypes = {
  t: PropTypes.func.isRequired,
  value: PropTypes.shape({
    range: PropTypes.string,
    date_from: PropTypes.string,
    date_to: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
}

export default DateRangeFilter
